/* Remy+Roo CRO Cart Drawer — v2.0 */
(() => {
  'use strict';

  const ROOT_ID = 'RRDrawerRoot';
  const SECTION_HANDLE = 'rr-cart-drawer';
  const INTERNAL_HEADER = 'X-RR-Cart';
  const SHOPIFY_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Keep a clean reference that cannot recurse through our fetch observer.
  const baseFetch = window.fetch.bind(window);

  const getDrawer = () => document.getElementById(ROOT_ID);
  const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

  function internalHeaders(extra = {}) {
    const headers = new Headers(extra || {});
    headers.set(INTERNAL_HEADER, '1');
    return headers;
  }

  async function fetchCart() {
    const response = await baseFetch(`${SHOPIFY_ROOT}cart.js?_rr=${Date.now()}`, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: internalHeaders({
        'Accept': 'application/json'
      })
    });

    if (!response.ok) throw new Error(`Could not fetch cart (${response.status})`);
    return response.json();
  }

  function cartFingerprint(cart) {
    if (!cart || !Array.isArray(cart.items)) return '';
    return cart.items
      .map((item) => `${item.key}:${item.quantity}`)
      .sort()
      .join('|');
  }

  async function waitForCartChange(beforeFingerprint, timeout = 5000) {
    const started = Date.now();

    while (Date.now() - started < timeout) {
      try {
        const cart = await fetchCart();
        const fingerprint = cartFingerprint(cart);
        if (fingerprint !== beforeFingerprint) return cart;
      } catch (error) {
        console.warn('[Remy+Roo Cart] Cart poll failed', error);
      }
      await sleep(140);
    }

    return null;
  }

  class RRCartDrawer extends HTMLElement {
    constructor() {
      super();
      this.lastFocused = null;
      this.boundKeydown = this.onKeydown.bind(this);
      this.sanitizing = false;
      this.refreshing = false;
    }

    connectedCallback() {
      this.bindEvents();
      window.setTimeout(() => this.sanitizeGiftLines(), 0);
    }

    bindEvents() {
      this.querySelectorAll('[data-rr-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });

      this.querySelectorAll('[data-rr-remove]').forEach((button) => {
        button.addEventListener('click', () => this.changeLine(button.dataset.rrRemove, 0, button));
      });

      this.querySelectorAll('[data-rr-qty-minus]').forEach((button) => {
        button.addEventListener('click', () => {
          const line = button.closest('[data-rr-line]');
          const current = Number(line?.dataset.lineQuantity || 1);
          this.changeLine(button.dataset.rrQtyMinus, Math.max(0, current - 1), button);
        });
      });

      this.querySelectorAll('[data-rr-qty-plus]').forEach((button) => {
        button.addEventListener('click', () => {
          const line = button.closest('[data-rr-line]');
          const current = Number(line?.dataset.lineQuantity || 1);
          this.changeLine(button.dataset.rrQtyPlus, current + 1, button);
        });
      });

      this.querySelectorAll('[data-rr-add-gift]').forEach((button) => {
        button.addEventListener('click', () => this.addGift(button));
      });

      this.querySelectorAll('[data-rr-add-rec]').forEach((button) => {
        button.addEventListener('click', () => this.addRecommendation(button));
      });

      this.querySelectorAll('[data-rr-variant-select]').forEach((select) => {
        if (select.tagName !== 'SELECT') return;
        select.addEventListener('change', () => this.updateCardPrice(select));
      });

      this.querySelectorAll('a[href="#rr-free-gifts"]').forEach((link) => {
        link.addEventListener('click', (event) => {
          event.preventDefault();
          this.querySelector('#rr-free-gifts')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
    }

    open() {
      this.lastFocused = document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('rr-cart-open');
      document.addEventListener('keydown', this.boundKeydown);
      window.requestAnimationFrame(() => this.querySelector('.rr-drawer__panel')?.focus());
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('rr-cart-open');
      document.removeEventListener('keydown', this.boundKeydown);
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') this.lastFocused.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape') this.close();
    }

    setBusy(button, busy, label = 'Updating…') {
      if (!button) return;

      if (busy) {
        if (!button.dataset.rrOldText) button.dataset.rrOldText = button.textContent;
        button.textContent = label;
        button.disabled = true;
      } else {
        button.textContent = button.dataset.rrOldText || button.textContent;
        button.disabled = false;
        delete button.dataset.rrOldText;
      }
    }

    async internalFetch(url, options = {}) {
      const headers = internalHeaders(options.headers || {});
      return baseFetch(url, {
        ...options,
        credentials: 'same-origin',
        cache: options.method && options.method !== 'GET' ? 'no-store' : (options.cache || 'no-store'),
        headers
      });
    }

    async changeLine(key, quantity, button) {
      this.setBusy(button, true, '…');

      try {
        const response = await this.internalFetch(`${SHOPIFY_ROOT}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });

        if (!response.ok) throw new Error(`Could not update cart line (${response.status})`);
        await this.refresh(true);
      } catch (error) {
        console.error('[Remy+Roo Cart]', error);
        this.setBusy(button, false);
      }
    }

    getSelectedVariant(button) {
      const card = button.closest('.rr-mini-product');
      return card?.querySelector('[data-rr-variant-select]')?.value || null;
    }

    updateCardPrice(select) {
      const card = select.closest('.rr-mini-product');
      const priceWrap = card?.querySelector('[data-rr-card-price]');
      const option = select.selectedOptions?.[0];
      if (!priceWrap || !option?.dataset.price) return;

      if (select.dataset.giftCard === 'true') {
        const strike = priceWrap.querySelector('s');
        if (strike) strike.textContent = option.dataset.price;
      } else {
        const strong = priceWrap.querySelector('strong');
        if (strong) strong.textContent = option.dataset.price;
      }
    }

    async addGift(button) {
      const variantId = this.getSelectedVariant(button);
      const giftType = button.dataset.giftType;
      if (!variantId || !giftType) return;

      this.setBusy(button, true, 'Adding…');

      try {
        const response = await this.internalFetch(`${SHOPIFY_ROOT}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: Number(variantId),
              quantity: 1,
              properties: {
                '_rr_free_gift': 'true',
                '_rr_gift_type': giftType,
                '_rr_gift_source': 'best-selling'
              }
            }]
          })
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(`Could not add free gift (${response.status}): ${text}`);
        }

        await this.refresh(true);
      } catch (error) {
        console.error('[Remy+Roo Cart]', error);
        this.setBusy(button, false);
      }
    }

    async addRecommendation(button) {
      const variantId = this.getSelectedVariant(button);
      if (!variantId) return;

      this.setBusy(button, true, 'Adding…');

      try {
        const response = await this.internalFetch(`${SHOPIFY_ROOT}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }]
          })
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(`Could not add product (${response.status}): ${text}`);
        }

        await this.refresh(true);
      } catch (error) {
        console.error('[Remy+Roo Cart]', error);
        this.setBusy(button, false);
      }
    }

    getState() {
      const node = this.querySelector('[data-rr-cart-state]');
      if (!node) return null;

      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.warn('[Remy+Roo Cart] Invalid drawer state', error);
        return null;
      }
    }

    async sanitizeGiftLines() {
      if (this.sanitizing) return;

      const state = this.getState();
      if (!state || !Array.isArray(state.giftLines) || !state.giftLines.length) return;

      const limits = {
        'Single': Number(state.allowedFreeSingles || 0),
        '2-Pack': Number(state.allowedFreeTwoPacks || 0)
      };

      const used = { 'Single': 0, '2-Pack': 0 };
      const corrections = [];

      state.giftLines.forEach((line) => {
        const type = line.type;
        const quantity = Number(line.quantity || 0);

        if (!(type in limits)) {
          corrections.push({ key: line.key, quantity: 0 });
          return;
        }

        const remaining = Math.max(0, limits[type] - used[type]);
        const keep = Math.min(quantity, remaining);

        if (keep !== quantity) corrections.push({ key: line.key, quantity: keep });
        used[type] += keep;
      });

      if (!corrections.length) return;

      this.sanitizing = true;

      try {
        for (const correction of corrections) {
          const response = await this.internalFetch(`${SHOPIFY_ROOT}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: correction.key, quantity: correction.quantity })
          });

          if (!response.ok) throw new Error('Could not remove invalid gift');
        }

        await this.refresh(this.classList.contains('is-open'));
      } catch (error) {
        console.error('[Remy+Roo Cart] Gift cleanup failed', error);
      } finally {
        this.sanitizing = false;
      }
    }

    async fetchFreshMarkup() {
      // IMPORTANT: request the *actual rendered section ID*, not merely the section type.
      // This is the core fix for the stale drawer issue.
      const sectionId = this.dataset.sectionId || this.getAttribute('data-section-id') || SECTION_HANDLE;

      // 1) Shopify multi-section rendering API (JSON)
      try {
        const url = new URL(SHOPIFY_ROOT, window.location.origin);
        url.searchParams.set('sections', sectionId);
        url.searchParams.set('_rr', String(Date.now()));

        const response = await this.internalFetch(url.toString(), {
          method: 'GET',
          headers: { 'Accept': 'application/json' }
        });

        if (response.ok) {
          const data = await response.json();
          const html = data && (data[sectionId] || data[SECTION_HANDLE]);
          if (html) return html;
        }
      } catch (error) {
        console.warn('[Remy+Roo Cart] JSON section render failed; trying fallback', error);
      }

      // 2) Shopify single-section rendering API (raw HTML fallback)
      const fallback = new URL(SHOPIFY_ROOT, window.location.origin);
      fallback.searchParams.set('section_id', sectionId);
      fallback.searchParams.set('_rr', String(Date.now()));

      const response = await this.internalFetch(fallback.toString(), {
        method: 'GET',
        headers: { 'Accept': 'text/html' }
      });

      if (!response.ok) throw new Error(`Could not render cart drawer (${response.status})`);
      const html = await response.text();
      if (!html || !html.includes(ROOT_ID)) throw new Error('Cart drawer section returned no usable markup');
      return html;
    }

    async refresh(keepOpen = false) {
      if (this.refreshing) return true;
      this.refreshing = true;

      const shouldOpen = keepOpen || this.classList.contains('is-open');

      try {
        const html = await this.fetchFreshMarkup();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.getElementById(ROOT_ID);

        if (!fresh) throw new Error('Updated drawer markup not found');

        // Preserve open state visually while swapping the entire stateful drawer.
        if (shouldOpen) {
          fresh.classList.add('is-open');
          fresh.setAttribute('aria-hidden', 'false');
        }

        this.replaceWith(fresh);

        const next = getDrawer();
        if (!next) throw new Error('Updated drawer was not mounted');

        if (shouldOpen) {
          document.body.classList.add('rr-cart-open');
          next.open();
        }

        await updateHeaderCount();

        document.dispatchEvent(new CustomEvent('rr:cart:updated', { detail: { source: 'rr-drawer' } }));
        window.dispatchEvent(new CustomEvent('rr:cart:updated', { detail: { source: 'rr-drawer' } }));

        return true;
      } catch (error) {
        console.error('[Remy+Roo Cart] Drawer refresh failed', error);
        throw error;
      } finally {
        this.refreshing = false;
      }
    }
  }

  if (!customElements.get('rr-cart-drawer')) {
    customElements.define('rr-cart-drawer', RRCartDrawer);
  }

  async function updateHeaderCount() {
    try {
      const cart = await fetchCart();
      const count = cart.item_count;

      const selectors = [
        '[data-cart-count]',
        '.cart-link__count',
        '.cart-count',
        '.header-cart-count',
        '[data-cart-count-bubble]',
        '.cart-count-bubble span',
        '.cart-count-bubble'
      ];

      selectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach((el) => {
          if (el.closest(`#${ROOT_ID}`)) return;

          if (
            el.children.length === 0 ||
            /count/i.test(el.className || '') ||
            el.hasAttribute('data-cart-count')
          ) {
            el.textContent = String(count);
          }

          el.hidden = count < 1;
        });
      });
    } catch (error) {
      console.warn('[Remy+Roo Cart] Could not update header count', error);
    }
  }

  function bindGlobalOpeners() {
    if (window.__rrDrawerOpenerBound) return;
    window.__rrDrawerOpenerBound = true;

    document.addEventListener('click', (event) => {
      const drawer = getDrawer();
      if (!drawer) return;
      if (event.target.closest(`#${ROOT_ID}`)) return;

      const opener = event.target.closest([
        '[data-rr-cart-open]',
        'a[href="/cart"]',
        'a[href$="/cart"]',
        'a[href*="/cart?"]',
        '[data-cart-drawer-toggle]',
        '[data-cart-toggle]'
      ].join(','));

      if (!opener) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      event.preventDefault();
      event.stopPropagation();
      drawer.open();
    }, true);
  }

  function watchAddToCartForms() {
    if (window.__rrDrawerFormWatchBound) return;
    window.__rrDrawerFormWatchBound = true;

    // This solves themes that cached window.fetch before this script loaded.
    // We do not interfere with the theme submission; we simply detect when
    // Shopify's cart actually changes and then re-render/open our drawer.
    document.addEventListener('submit', async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const action = String(form.getAttribute('action') || form.action || '');
      if (!/\/cart\/add(?:\.js)?(?:\?|$)/.test(action)) return;
      if (form.closest(`#${ROOT_ID}`)) return;

      let before = '';

      try {
        before = cartFingerprint(await fetchCart());
      } catch (error) {
        console.warn('[Remy+Roo Cart] Could not snapshot cart before add', error);
      }

      // Let the theme perform its normal add-to-cart first.
      await sleep(80);
      const changed = await waitForCartChange(before, 5000);

      if (changed) {
        try {
          await getDrawer()?.refresh(true);
        } catch (error) {
          console.error('[Remy+Roo Cart] Could not open drawer after product add', error);
        }
      }
    }, true);
  }

  function watchThemeCartMutations() {
    if (window.__rrDrawerNetworkBound) return;
    window.__rrDrawerNetworkBound = true;

    const nativeFetch = window.fetch.bind(window);

    window.fetch = async (...args) => {
      const input = args[0];
      const init = args[1] || {};
      const url = typeof input === 'string' ? input : (input?.url || '');
      const headers = new Headers(init.headers || (typeof input !== 'string' ? input?.headers : undefined));
      const isInternal = headers.get(INTERNAL_HEADER) === '1';
      const isMutation = /\/cart\/(add|change|update)(\.js)?(?:\?|$)/.test(String(url));

      const response = await nativeFetch(...args);

      if (!isInternal && isMutation && response.ok) {
        window.setTimeout(async () => {
          try {
            await getDrawer()?.refresh(true);
          } catch (error) {
            console.error('[Remy+Roo Cart] Theme cart refresh failed', error);
          }
        }, 60);
      }

      return response;
    };

    const nativeOpen = XMLHttpRequest.prototype.open;
    const nativeSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function(method, url, ...rest) {
      this.__rrCartMutation = /\/cart\/(add|change|update)(\.js)?(?:\?|$)/.test(String(url));
      return nativeOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function(...args) {
      if (this.__rrCartMutation) {
        this.addEventListener('load', () => {
          if (this.status >= 200 && this.status < 300) {
            window.setTimeout(async () => {
              try {
                await getDrawer()?.refresh(true);
              } catch (error) {
                console.error('[Remy+Roo Cart] XHR cart refresh failed', error);
              }
            }, 60);
          }
        }, { once: true });
      }

      return nativeSend.apply(this, args);
    };
  }

  function bindCommonThemeEvents() {
    if (window.__rrDrawerEventWatchBound) return;
    window.__rrDrawerEventWatchBound = true;

    // Only events that are commonly emitted specifically after an ADD.
    // We intentionally do not listen to generic `cart:updated` because the
    // drawer itself may emit similar events and that can create loops.
    const events = [
      'product:added',
      'ajaxProduct:added',
      'cart:add',
      'cart:item-added'
    ];

    events.forEach((name) => {
      document.addEventListener(name, () => {
        window.setTimeout(async () => {
          try {
            await getDrawer()?.refresh(true);
          } catch (error) {
            console.error(`[Remy+Roo Cart] ${name} refresh failed`, error);
          }
        }, 60);
      });
    });
  }

  bindGlobalOpeners();
  watchAddToCartForms();
  watchThemeCartMutations();
  bindCommonThemeEvents();
})();
